<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { writable, get } from 'svelte/store';
  import { selectedStudy } from '$stores/selectionStore';
  import { theme } from '$stores/themeStore';
  import { userRole, isReadOnly } from '$stores/sessionStore';

  const dispatch = createEventDispatcher();
  export let showForm = false;
  export let editForm = null;

  // Using local API endpoints instead of direct API access
  const API_BASE_URL = '/api/proxy';

  const FORM_TYPES = [
    'SubjectForm',
    'SiteForm',
    'StudyForm'
  ];

  // Lookup options for new fields
  let formTypeOptions = [];
  let userTypeOptions = [];
  let formModelOptions = [];
  let ageRangeOptions = [];
  let weightRangeOptions = [];
  let studyLanguages = [];

  // Determine if a translation's JSON is non-empty (not just '{}', '[]', or blank)
  function hasNonEmptyJson(jsonLike) {
    if (jsonLike == null) return false;
    if (typeof jsonLike === 'string') {
      const trimmed = jsonLike.trim();
      if (trimmed === '' || trimmed === '{}' || trimmed === '[]') return false;
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.length > 0;
        if (parsed && typeof parsed === 'object') return Object.keys(parsed).length > 0;
        return Boolean(parsed);
      } catch (e) {
        // If it's not valid JSON but non-empty string, treat as present
        return true;
      }
    }
    if (Array.isArray(jsonLike)) return jsonLike.length > 0;
    if (typeof jsonLike === 'object') return Object.keys(jsonLike).length > 0;
    return Boolean(jsonLike);
  }

  // Normalize potential ObjectId-like values to plain strings for comparison
  function normalizeIdForMarking(id) {
    if (!id) return '';
    if (typeof id === 'string') return id;
    if (id.$oid) return id.$oid;
    if (id.toHexString) return id.toHexString();
    const s = id.toString ? id.toString() : '';
    const match = s.match(/[a-fA-F0-9]{24}/);
    return match ? match[0] : s;
  }

  // On initial open with an editForm, use its translations; otherwise use the live store
  $: effectiveTranslations = (editForm && !initializedEdit)
    ? (editForm?.translations || [])
    : ($formData?.translations || []);

  // Mark languages that have non-empty translations for the currently loaded form only
  $: markedLanguageGUIDs = new Set(
    (effectiveTranslations || [])
      .filter(t => t && normalizeIdForMarking(t.languageGUID) && hasNonEmptyJson(t.form))
      .map(t => String(normalizeIdForMarking(t.languageGUID)))
  );

  let formData = writable({
    name: '',
    type: 'SubjectForm', // Hidden field, kept for backward compatibility
    studyGUID: $selectedStudy?._id,
    active: true,
    form: '{}',
    exportLabel: '',
    // New fields
    typeGUID: '',
    userTypeGUIDs: [],
    modelGUID: '',
    rangeGUIDs: []
  });

  let formJsonError = '';
  let selectedLanguageGUID = '';
  let previousSelectedLanguageGUID = '';
  let initializedEdit = false;
  
  function computePreferredLanguageGUID() {
    const en = studyLanguages.find(l => l.code === 'en_US');
    if (en && en.languageGUID) return en.languageGUID;
    const def = studyLanguages.find(l => l.languageDefault);
    if (def && def.languageGUID) return def.languageGUID;
    return studyLanguages[0]?.languageGUID || '';
  }
  
  $: if (editForm && !initializedEdit) {
    const formattedForm = { ...editForm };
    
    // Normalize translations array from legacy single-language fields if needed
    const prettify = (jsonStr) => {
      try {
        if (!jsonStr) return '{}';
        const parsed = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr;
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        return jsonStr;
      }
    };

    // Normalize any languageGUID/ObjectId shapes to plain strings
    const normalizeId = (id) => {
      if (!id) return id;
      if (typeof id === 'string') return id;
      if (id.$oid) return id.$oid;
      if (id.toHexString) return id.toHexString();
      const s = id.toString ? id.toString() : '';
      const match = s.match(/[a-fA-F0-9]{24}/);
      return match ? match[0] : s;
    };

    const normalizedTranslations = Array.isArray(formattedForm.translations) && formattedForm.translations.length > 0
      ? formattedForm.translations
      : (formattedForm.languageGUID || formattedForm.form)
        ? [{ languageGUID: normalizeId(formattedForm.languageGUID) || '', form: formattedForm.form || '{}' }]
        : [];

    formattedForm.translations = normalizedTranslations.map(t => ({
      ...t,
      languageGUID: normalizeId(t.languageGUID),
      form: prettify(t.form)
    }));
    
    // Ensure arrays are properly initialized for new fields
    if (!formattedForm.userTypeGUIDs) formattedForm.userTypeGUIDs = [];
    if (!formattedForm.rangeGUIDs) formattedForm.rangeGUIDs = [];
    
    // Determine selected language and load its form JSON into editor
    const preferredGUID = computePreferredLanguageGUID();
    const hasPreferred = preferredGUID && formattedForm.translations.some(t => String(t.languageGUID) === String(preferredGUID));
    selectedLanguageGUID = hasPreferred
      ? preferredGUID
      : (normalizeId(formattedForm.languageGUID) || formattedForm.translations[0]?.languageGUID || '');
    previousSelectedLanguageGUID = selectedLanguageGUID;

    const currentTranslation = formattedForm.translations.find(t => String(t.languageGUID) === String(selectedLanguageGUID)) || formattedForm.translations[0];
    const currentFormJson = currentTranslation?.form || formattedForm.form || '{}';

    formData.set({
      ...formattedForm,
      form: prettify(currentFormJson)
    });
    initializedEdit = true;
  }

  $: if (showForm && !editForm) {
    resetFormData();
    initializedEdit = false;
  }

  async function fetchLookupData() {
    try {
      // Fetch all required lookup data in parallel
      const [formTypeRes, userTypeRes, formModelRes, ageRangeRes, weightRangeRes, studyLangRes] = await Promise.all([
        fetch(`${API_BASE_URL}/lookups/FORMTYPE`),
        fetch(`${API_BASE_URL}/lookups/FORMUSERTYPE`),
        fetch(`${API_BASE_URL}/lookups/FORMMODEL`),
        fetch(`${API_BASE_URL}/lookups/AGERANGE`),
        fetch(`${API_BASE_URL}/lookups/WEIGHTRANGE`),
        $selectedStudy?._id ? fetch(`${API_BASE_URL}/languages/study/${$selectedStudy._id}`) : Promise.resolve({ ok: true, json: async () => [] })
      ]);

      // Parse responses
      const [formTypeData, userTypeData, formModelData, ageRangeData, weightRangeData, studyLangData] = await Promise.all([
        formTypeRes.json(),
        userTypeRes.json(),
        formModelRes.json(),
        ageRangeRes.json(),
        weightRangeRes.json(),
        studyLangRes.json()
      ]);

      // Set options
      formTypeOptions = formTypeData.data || [];
      userTypeOptions = userTypeData.data || [];
      formModelOptions = formModelData.data || [];
      ageRangeOptions = ageRangeData.data || [];
      weightRangeOptions = weightRangeData.data || [];
      // Normalize study languages: make sure languageGUID is a plain string
      studyLanguages = (Array.isArray(studyLangData) ? studyLangData : []).map(l => {
        const guid = l?.languageGUID;
        let normalized = guid;
        if (guid && typeof guid === 'object') {
          normalized = guid.$oid || (guid.toHexString ? guid.toHexString() : (guid.toString ? guid.toString() : ''));
          // Extract 24-hex if needed from stringified forms
          if (typeof normalized === 'string') {
            const match = normalized.match(/[a-fA-F0-9]{24}/);
            if (match) normalized = match[0];
          }
        } else if (guid && guid.$oid) {
          normalized = guid.$oid;
        }
        return {
          ...l,
          languageGUID: normalized
        };
      });

    } catch (error) {
      console.error('Error fetching lookup data:', error);
    }
  }

  onMount(async () => {
    await fetchLookupData();
  });

  function resetFormData() {
    // Find default options
    const defaultFormType = formTypeOptions.find(opt => opt.default);
    const defaultFormModel = formModelOptions.find(opt => opt.default);
    const defaultUserTypes = userTypeOptions.filter(opt => opt.default);
    const defaultAgeRanges = ageRangeOptions.filter(opt => opt.default);
    const defaultWeightRanges = weightRangeOptions.filter(opt => opt.default);
    const defaultStudyLanguage = studyLanguages.find(l => l.languageDefault) || studyLanguages[0];
    
    // Combine all default range options
    const defaultRangeGUIDs = [
      ...defaultAgeRanges.map(opt => opt.id),
      ...defaultWeightRanges.map(opt => opt.id)
    ];

    // Prefer en_US if available
    selectedLanguageGUID = computePreferredLanguageGUID();
    previousSelectedLanguageGUID = selectedLanguageGUID;

    formData.set({
      name: '',
      type: 'SubjectForm', // Keep default for backward compatibility
      studyGUID: $selectedStudy?._id,
      active: true,
      form: '{}',
      exportLabel: '',
      // New fields with defaults
      typeGUID: defaultFormType?.id || '',
      userTypeGUIDs: defaultUserTypes.map(opt => opt.id) || [],
      modelGUID: defaultFormModel?.id || '',
      rangeGUIDs: defaultRangeGUIDs || [],
      languageGUID: selectedLanguageGUID,
      translations: selectedLanguageGUID ? [{ languageGUID: selectedLanguageGUID, form: '{}' }] : []
    });
    formJsonError = '';
  }

  function close() {
    dispatch('close');
    initializedEdit = false;
  }

  function validateFormJson() {
    try {
      JSON.parse($formData.form);
      formJsonError = '';
      return true;
    } catch (e) {
      formJsonError = e.message;
      return false;
    }
  }

  function getOrCreateTranslation(langGUID) {
    const current = get(formData);
    if (!current.translations || !Array.isArray(current.translations)) {
      current.translations = [];
    }
    let idx = current.translations.findIndex(t => String(t.languageGUID) === String(langGUID));
    if (idx === -1) {
      current.translations.push({ languageGUID: langGUID, form: '{}' });
      idx = current.translations.length - 1;
    }
    return { current, idx };
  }

  function handleLanguageChange(e) {
    const newLang = e.target.value;
    const current = get(formData);
    // Save current editor JSON back to previous selected translation
    if (previousSelectedLanguageGUID) {
      const prev = getOrCreateTranslation(previousSelectedLanguageGUID);
      prev.current.translations[prev.idx].form = current.form || '{}';
    }
    // Load new language's JSON into editor
    const next = getOrCreateTranslation(newLang);
    const newForm = next.current.translations[next.idx].form || '{}';
    // Preserve current field values, but update language and form
    formData.set({ ...current, translations: next.current.translations, form: newForm, languageGUID: newLang });
    selectedLanguageGUID = newLang;
    previousSelectedLanguageGUID = newLang;
  }

  async function handleSubmit() {
    if (!validateFormJson()) {
      return;
    }
    
    const current = get(formData);
    const formJsonFromEditor = current.form || '{}';
    
    // Ensure form is valid JSON
    let normalizedFormJson;
    try {
      const parsedForm = JSON.parse(formJsonFromEditor);
      normalizedFormJson = JSON.stringify(parsedForm);
    } catch (e) {
      formJsonError = e.message;
      return;
    }
    
    // Build translations array with the edited form JSON in the correct translation
    let translations = Array.isArray(current.translations) ? [...current.translations] : [];
    if (selectedLanguageGUID) {
      const idx = translations.findIndex(t => String(t.languageGUID) === String(selectedLanguageGUID));
      if (idx >= 0) {
        translations[idx] = { ...translations[idx], form: normalizedFormJson };
      } else {
        translations.push({ languageGUID: selectedLanguageGUID, form: normalizedFormJson });
      }
    } else if (translations.length > 0) {
      // Fallback: update first translation when no language selected
      translations[0] = { ...translations[0], form: normalizedFormJson };
    }
    
    // Build payload with explicit form field (top-level for backward compatibility)
    const form = {
      ...current,
      form: normalizedFormJson,
      translations,
      languageGUID: selectedLanguageGUID || current.languageGUID
    };
    
    dispatch('submit', form);
    close();
  }
</script>

{#if showForm}
  <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
    <!-- svelte-ignore a11y-click-events-have-key-events --> 
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={close}></div>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
      class="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg w-full max-w-4xl relative z-60"
      on:click|stopPropagation
    >
      <header class="flex justify-between items-start mb-4">
        <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100">
          {editForm ? 'Edit Form' : 'New Form'}
        </h2>
        <button
          on:click={close}
          class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
        >
          &times;
        </button>
      </header>

      <form on:submit|preventDefault={handleSubmit}>
        <div class="flex flex-col space-y-4">
          <!-- Basic Information -->
          <div class="grid grid-cols-2 gap-4">
            <!-- Name -->
            <div>
              <label for="name" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">
                Name *
              </label>
              <input
                id="name"
                type="text"
                bind:value={$formData.name}
                required
                class="h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100"
              />
            </div>

            <!-- Export Label -->
            <div>
              <label for="exportLabel" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">
                Export Label
              </label>
              <input
                id="exportLabel"
                type="text"
                bind:value={$formData.exportLabel}
                class="h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100"
              />
            </div>
          </div>

        <!-- Form Type and Model -->
        <div class="grid grid-cols-3 gap-4">
            <!-- Form Type -->
            <div>
              <label for="typeGUID" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">
                Form Type *
              </label>
              <select
                id="typeGUID"
                bind:value={$formData.typeGUID}
                required
                class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
              >
                <option value="">Select Form Type</option>
                {#each formTypeOptions as option}
                  <option value={option.id}>{option.label}</option>
                {/each}
              </select>
            </div>

            <!-- Form Model -->
            <div>
              <label for="modelGUID" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">
                Form Model *
              </label>
              <select
                id="modelGUID"
                bind:value={$formData.modelGUID}
                required
                class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
              >
                <option value="">Select Form Model</option>
                {#each formModelOptions as option}
                  <option value={option.id}>{option.label}</option>
                {/each}
              </select>
            </div>

          <!-- Language -->
          <div>
            <label for="languageGUID" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">
              Language
            </label>
            <select
              id="languageGUID"
              bind:value={selectedLanguageGUID}
              on:change={handleLanguageChange}
              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
            >
              <option value="">Select Language</option>
              {#each studyLanguages as lang}
                <option value={lang.languageGUID}>{lang.flag} {lang.language} ({lang.code}){markedLanguageGUIDs.has(String(lang.languageGUID)) ? ' •' : ''}</option>
              {/each}
            </select>
          </div>
          </div>

          <!-- User Types, Age Ranges, Weight Ranges -->
          <div class="grid grid-cols-2 gap-4">
            <!-- User Types -->
            <div>
              <!-- svelte-ignore a11y-label-has-associated-control -->
              <label class="block text-xs pb-1 text-gray-700 dark:text-gray-200">
                User Types *
              </label>
              <div class="max-h-32 overflow-y-auto border p-2 rounded text-sm dark:bg-gray-700 dark:border-gray-600 space-y-1">
                {#each userTypeOptions as option}
                  <label class="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      value={option.id}
                      checked={$formData.userTypeGUIDs.includes(option.id)}
                      on:change={(e) => {
                        if (e.target.checked) {
                          $formData.userTypeGUIDs = [...$formData.userTypeGUIDs, option.id];
                        } else {
                          $formData.userTypeGUIDs = $formData.userTypeGUIDs.filter(id => id !== option.id);
                        }
                      }}
                      class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span class="text-xs text-gray-700 dark:text-gray-200">{option.label}</span>
                  </label>
                {/each}
              </div>
            </div>

            <!-- Age Ranges -->
            <div>
              <!-- svelte-ignore a11y-label-has-associated-control -->
              <label class="block text-xs pb-1 text-gray-700 dark:text-gray-200">
                Age Ranges
              </label>
              <div class="max-h-32 overflow-y-auto border p-2 rounded text-sm dark:bg-gray-700 dark:border-gray-600 space-y-1">
                {#each ageRangeOptions as option}
                  <label class="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      value={option.id}
                      checked={$formData.rangeGUIDs.includes(option.id)}
                      on:change={(e) => {
                        if (e.target.checked) {
                          $formData.rangeGUIDs = [...$formData.rangeGUIDs, option.id];
                        } else {
                          $formData.rangeGUIDs = $formData.rangeGUIDs.filter(id => id !== option.id);
                        }
                      }}
                      class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span class="text-xs text-gray-700 dark:text-gray-200">{option.label}</span>
                  </label>
                {/each}
              </div>
            </div>
          </div>

          <!-- Active Status -->
          <div class="flex items-center">
            <input
              id="active"
              type="checkbox"
              bind:checked={$formData.active}
              class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label for="active" class="ml-2 block text-sm text-gray-700 dark:text-gray-200">
              Active
            </label>
          </div>

          <!-- Form JSON -->
          <div>
            <label for="form" class="block text-xs pb-1 text-gray-700 dark:text-gray-200">
              Form JSON
            </label>
            <textarea
              id="form"
              bind:value={$formData.form}
              class="w-full border px-2 py-1 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 font-mono"
              rows="20"
              on:blur={validateFormJson}
            ></textarea>
            {#if formJsonError}
              <p class="text-red-500 text-xs mt-1">{formJsonError}</p>
            {/if}
          </div>
        </div>

        <!-- Form Actions -->
        <div class="flex justify-end space-x-2 mt-6">
          <button
            type="button"
            on:click={close}
            class="bg-gray-500 text-white py-1 px-2 rounded hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="bg-blue-500 text-white py-1 px-2 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title={editForm ? 'Update form' : 'Create form'}
            disabled={isReadOnly()}
          >
            {editForm ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if} 